import React, { useState } from "react";

import { Grid } from "@material-ui/core";
import { useSelector } from "react-redux";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  ZoomyDialog,
} from "components/MaterialDialog";
import {
  Button,
  CollapseHeader,
  FormCollapse,
  SecondaryButton,
} from "components/Form";
import { Text } from "components/Text";

import { EContractType } from "constants/eContract";

import { EContractSpecialConditionsEditor } from "./eContractSpecialConditionsEditor";
import { getActiveLivestockAgentDeployment } from "selectors";

interface EContractConditionsModalProps {
  agreement: EContractAgreement;
  conditions: EContractSpecialCondition[];
  onClose: () => void;
  onGenerateContract: () => void;
}

function getConditionsHelperText(
  eContractType: string | null | undefined,
): string | null {
  switch (eContractType) {
    case EContractType.ALPA:
      return '"Payment Arrangements" conditions appear in their own section on the contract. "Other" conditions appear in the Special Conditions block without a category prefix. All remaining conditions appear as "Category: description" in the Special Conditions block.';
    case EContractType.RMA:
      return '"Compliance" conditions appear in the NLIS section of the contract. "Other" conditions appear in the Special Conditions block without a category prefix. All remaining conditions appear as "Category: description" in the Special Conditions block.';
    default:
      return null;
  }
}

export const EContractConditionsModal = ({
  agreement,
  conditions,
  onClose,
  onGenerateContract,
}: EContractConditionsModalProps): React.JSX.Element => {
  const [isConditionsOpen, setIsConditionsOpen] = useState(true);
  const [isAddingCondition, setIsAddingCondition] = useState(false);

  const deployment = useSelector(getActiveLivestockAgentDeployment);
  const eContractType = deployment?.deploymentSettings?.eContractType || null;

  const helperText = getConditionsHelperText(eContractType);

  const handleGenerate = () => {
    onGenerateContract();
    onClose();
  };

  return (
    <ZoomyDialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle onClose={onClose}>Draft New eContract</DialogTitle>
      <DialogContent dividers form>
        <FormCollapse
          isOpen={isConditionsOpen}
          onToggle={() => setIsConditionsOpen(v => !v)}
          header={<CollapseHeader title="Special Conditions" />}
        >
          {helperText && (
            <Grid item xs={12}>
              <Text italic color="titleMedium">
                {helperText}
              </Text>
            </Grid>
          )}
          <Grid item xs={12}>
            <EContractSpecialConditionsEditor
              agreementId={agreement.id}
              conditions={conditions}
              onFormActiveChange={setIsAddingCondition}
            />
          </Grid>
        </FormCollapse>
      </DialogContent>
      <DialogActions>
        <SecondaryButton type="button" onClick={onClose}>
          Close
        </SecondaryButton>
        <Button
          type="button"
          onClick={handleGenerate}
          disabled={isAddingCondition}
        >
          Draft eContract
        </Button>
      </DialogActions>
    </ZoomyDialog>
  );
};
